// BUILD-042 (Oct 15 22:12) — a thin, lazily loaded wrapper around pdf.js so the Form Library "Set up signing" screen can draw the
// REAL pages of an uploaded PDF. pdf.js (pdfjs-dist, exact pin in package.json) is imported only when a setup screen is opened, so
// it adds nothing to the initial bundle. The worker file is bundled by Vite (?url) and served from our own origin: no CDN.
//
// Safety: font evaluation is disabled (isEvalSupported:false) and the viewer never runs document scripts. The bytes come from our own
// API (the exact stored version), never from a URL the page chooses.

export interface RenderedPage {
  /** Size of the page in PDF points (1/72 in) as pdf.js reports it. */
  widthPts: number;
  heightPts: number;
  /** CSS pixels per PDF point that the canvas was drawn at. */
  scale: number;
  cssWidth: number;
  cssHeight: number;
}

export interface LoadedPdf {
  numPages: number;
  /** Draw page `pageNo` (1-based) into `canvas` at `cssWidth` CSS pixels wide. A newer call cancels an older one on the same canvas. */
  renderPage(pageNo: number, canvas: HTMLCanvasElement, cssWidth: number): Promise<RenderedPage>;
  destroy(): void;
}

export function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

let pdfjsPromise: Promise<any> | null = null;
function loadPdfjs(): Promise<any> {
  if (!pdfjsPromise) {
    pdfjsPromise = (async () => {
      const [lib, worker] = await Promise.all([
        import('pdfjs-dist/legacy/build/pdf.mjs'),
        import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url'),
      ]);
      (lib as any).GlobalWorkerOptions.workerSrc = (worker as any).default;
      return lib as any;
    })();
    // A failed load must be retryable (e.g. a transient network error fetching the chunk).
    pdfjsPromise.catch(() => { pdfjsPromise = null; });
  }
  return pdfjsPromise;
}

// ── Drawing without ever sharing a canvas between two draws ───────────────────────────────────────────────────────────────────
// Oct 17 root cause of the page appearing UPSIDE DOWN after "Set up signing" was opened again: the screen can hold two LoadedPdf objects
// for the same bytes at once (a re-open, or React re-running the load effect), and each used to keep its OWN list of running draws. A draw
// from the second object therefore never cancelled the first object's draw on the same <canvas>. pdf.js applies the page transform to the
// canvas context while it draws, so two interleaved draws on one context (measured: starts 5 to 40 ms apart) leave the canvas flipped and the
// second draw fails. It is NOT a PDF rotation or coordinate problem: the pages involved are /Rotate 0 with matching crop and media boxes.
//
// Fix: every draw goes to its OWN private offscreen canvas (so no context is ever shared), the newest request for a visible canvas cancels
// older ones across ALL LoadedPdf objects (module-level bookkeeping), and a finished draw is copied onto the visible canvas only if it is
// still the newest. A superseded draw ends with the same RenderingCancelledException the screen already ignores.
const newestDraw = new WeakMap<HTMLCanvasElement, number>();
const runningDraw = new WeakMap<HTMLCanvasElement, { cancel: () => void }>();
let drawCounter = 0;
const cancelled = (): Error => Object.assign(new Error('Rendering cancelled'), { name: 'RenderingCancelledException' });

export async function loadPdf(bytes: Uint8Array): Promise<LoadedPdf> {
  const pdfjs = await loadPdfjs();
  const task = pdfjs.getDocument({ data: bytes.slice(), isEvalSupported: false, enableXfa: false });
  const doc = await task.promise;
  return {
    numPages: doc.numPages,
    async renderPage(pageNo, canvas, cssWidth) {
      const mine = ++drawCounter;
      newestDraw.set(canvas, mine);
      runningDraw.get(canvas)?.cancel();            // across every LoadedPdf, not just this one
      const page = await doc.getPage(pageNo);
      if (newestDraw.get(canvas) !== mine) throw cancelled();
      const base = page.getViewport({ scale: 1 });
      const scale = cssWidth / base.width;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const viewport = page.getViewport({ scale: scale * dpr });
      const cssHeight = base.height * scale;
      const off = document.createElement('canvas');
      off.width = Math.floor(viewport.width);
      off.height = Math.floor(viewport.height);
      const offCtx = off.getContext('2d');
      if (!offCtx) throw new Error('The page could not be drawn in this browser.');
      const renderTask = page.render({ canvasContext: offCtx, viewport });
      runningDraw.set(canvas, renderTask);
      try {
        await renderTask.promise;
      } catch (e: any) {
        if (e && e.name === 'RenderingCancelledException') throw e;
        throw new Error('This page could not be drawn.');
      } finally {
        if (runningDraw.get(canvas) === renderTask) runningDraw.delete(canvas);
      }
      if (newestDraw.get(canvas) !== mine) throw cancelled();
      canvas.width = off.width;
      canvas.height = off.height;
      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${cssHeight}px`;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('The page could not be drawn in this browser.');
      ctx.drawImage(off, 0, 0);
      return { widthPts: base.width, heightPts: base.height, scale, cssWidth, cssHeight };
    },
    destroy() { try { void doc.destroy(); } catch { /* already gone */ } },
  };
}
